import React, { useState } from 'react';
import Navigation from '../Navigation';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import { 
  Calendar,
  Phone,
  Clock,
  Globe,
  BarChart3,
  MessageCircle,
  Users,
  Shield,
  Zap,
  CheckCircle,
  ArrowRight,
  Play,
  Volume2,
  Star,
  PhoneCall,
  UserCheck,
  Settings,
  TrendingUp,
  Award,
  Headphones,
  Target,
  Check,
  Download,
  AlertTriangle,
  ThumbsUp,
  DollarSign,
  Pause,
  Plus,
  Minus
} from 'lucide-react';

const ConciergePage = () => {
  const [activeAudio, setActiveAudio] = useState(null);
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const handleDemo = () => {
    window.open('https://cal.com/smartaisolutions/vioreceptionist', '_blank');
  };

  const handleAudioPlay = (audioId) => {
    if (activeAudio === audioId) {
      setActiveAudio(null);
    } else {
      setActiveAudio(audioId);
    }
  };

  const toggleFAQ = (index) => {
    if (expandedFAQ === index) {
      setExpandedFAQ(null);
    } else {
      setExpandedFAQ(index);
    }
  };

  // Benefits list
  const benefits = [
    "Always Available 365 Days a Year",
    "Automates Your Appointments", 
    "Route Your Calls Smartly",
    "Reduces Your Costs",
    "Is Professional and Engaging",
    "Has a Human-Like Voice",
    "Never Take a Sick Day",
    "Speaks Multiple Languages"
  ];

  // Target audience list
  const targetAudience = [
    {
      icon: Phone,
      title: "Busy service-based businesses",
      description: "like restaurants, salons, and clinics that need a professional, always-on receptionist."
    },
    {
      icon: TrendingUp,
      title: "Scaling businesses", 
      description: "ready to automate and streamline without sacrificing customer service."
    },
    {
      icon: Clock,
      title: "Owners who want their time back",
      description: "while still capturing every opportunity."
    },
    {
      icon: Shield,
      title: "Professionals who care about compliance",
      description: "with secure, GDPR-compliant call handling."
    }
  ];

  // Audio demos
  const audioDemos = [
    {
      id: 'greeting',
      title: 'Professional Greeting and Call Transfer',
      description: 'Polly makes every first impression count with a warm, professional, and efficient 24/7 greeting—seamlessly handling calls, transferring to a human when needed.',
      audioUrl: 'https://customer-assets.emergentagent.com/job_vioqbase-showcase/artifacts/gsbfgkfi_Professional%20Greeting%20and%20Call%20Transfer.mp3'
    },
    {
      id: 'booking',
      title: 'Seamless Appointment Booking Process', 
      description: 'Polly makes scheduling effortless by handling bookings, reschedules, sending out payment links and cancellations with ease—no wait times, no hassle, just seamless scheduling.',
      audioUrl: 'https://customer-assets.emergentagent.com/job_vioqbase-showcase/artifacts/t6wztlfa_Seamless%20Appointment%20Booking%20Process.mp3'
    },
    {
      id: 'customer',
      title: 'Customer Enquires and FAQ\'s',
      description: 'Polly instantly answers both common and in-depth customer questions, providing accurate information. Whether it\'s simple FAQs or more detailed enquiries.',
      audioUrl: 'https://customer-assets.emergentagent.com/job_vioqbase-showcase/artifacts/8gqpwbz7_Customer%20Enquires%20and%20FAQ\'s.mp3'
    }
  ];

  // FAQ data
  const faqCategories = [
    {
      category: "General Questions",
      questions: [
        {
          q: "What is VioReceptionist?",
          a: "VioReceptionist is an AI-powered voice receptionist that answers calls, books appointments, handles FAQs, and provides 24/7 customer support."
        },
        {
          q: "How does VioReceptionist work?",
          a: "It uses AI to answer calls, provide information, take messages, and route inquiries—ensuring no missed calls or frustrated customers."
        },
        {
          q: "Is VioReceptionist available 24/7?",
          a: "Yes! Polly operates around the clock, handling calls anytime, even after business hours."
        },
        {
          q: "Can Polly understand multiple languages?",
          a: "Yes! Polly can communicate in multiple languages, ensuring your customers are always understood."
        },
        {
          q: "What businesses can benefit from VioReceptionist?",
          a: "Any business that receives phone calls—such as restaurants, salons, clinics, law firms, and service providers—can benefit from automated call handling."
        }
      ]
    },
    {
      category: "Call Handling & Features",
      questions: [
        {
          q: "What happens if a caller has a question Polly can't answer?",
          a: "Polly can escalate the call to a human agent, take a message, or offer a callback option."
        },
        {
          q: "Can Polly take messages and send them to me?",
          a: "Yes! Polly can transcribe voicemails and send them via email or SMS."
        },
        {
          q: "Does Polly book appointments?",
          a: "Absolutely! Polly can access your calendar and book appointments in real-time."
        },
        {
          q: "Can Polly provide information about my business?",
          a: "Yes! Polly can answer questions about your services, hours, location, pricing, and more."
        },
        {
          q: "Can I customize Polly's responses?",
          a: "Yes! You can customize Polly's script, tone, and responses to match your brand and business needs."
        }
      ]
    },
    {
      category: "Security & Reliability",
      questions: [
        {
          q: "Is my customer data safe with VioReceptionist?",
          a: "Absolutely! We use enterprise-grade security and are fully GDPR compliant to protect your customer data."
        },
        {
          q: "What happens if there's a technical issue?",
          a: "Our system has 99.9% uptime, and we provide 24/7 technical support to ensure smooth operations."
        },
        {
          q: "How reliable is VioReceptionist?",
          a: "VioReceptionist is built on enterprise-grade infrastructure with redundant systems to ensure maximum reliability and uptime."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#112d56] via-[#1a3d6b] to-[#112d56] text-white py-12 sm:py-16 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
        <div className="relative container mx-auto px-4 sm:px-6">
          <div className="text-center max-w-5xl mx-auto space-y-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold leading-tight tracking-tight">
              <span className="text-[#ff7a1f]">VioReceptionist</span> powered by Smart Parrots Your Virtual Receptionists That Work <span className="text-[#ff7a1f]">24/7</span> and Never Takes a Break.
            </h1>
            
            <div className="py-6">
              <Button 
                size="lg" 
                className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-8 sm:px-12 py-4 sm:py-6 text-lg sm:text-xl rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                onClick={handleDemo}
              >
                <Calendar className="mr-3 h-6 w-6" />
                SCHEDULE A DEMO
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Subheading Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-6xl mx-auto">
            <h2 className="text-3xl lg:text-5xl font-bold text-[#112d56] mb-6">
              Boost Efficiency, Automate Calls, and Elevate Customer Engagement with <span className="text-[#ff7a1f]">VioReceptionist</span>.
            </h2>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
              {/* Video Player Header */}
              <div className="bg-gradient-to-r from-[#112d56] to-[#1a3d6b] px-6 py-4 flex items-center space-x-3">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                </div>
                <div className="flex items-center space-x-2">
                  <Play className="h-4 w-4 text-white/70" />
                </div>
              </div>
              
              {/* Video Embed - Using correct VioReceptionist video ID */}
              <div className="bg-black relative">
                <div className="relative" style={{ paddingBottom: '56.25%' }}>
                  <iframe 
                    src="https://api.vadoo.tv/iframe_test?id=LgPmnpbRWHNpTIAXq8o10fv79sFG17jE" 
                    scrolling="no" 
                    style={{ 
                      border: 0, 
                      top: 0, 
                      left: 0, 
                      width: '100%', 
                      height: '100%', 
                      position: 'absolute',
                      overflow: 'hidden',
                      borderRadius: '5px'
                    }} 
                    allowFullScreen="1" 
                    allow="autoplay"
                    title="VioReceptionist Demo Video"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problems Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Are You Tired of Missed Calls, Lost Customers, and being overwhelmed by the number of calls you receive?
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
                Every missed call is a lost opportunity. Whether it's a potential customer, a repeat client, or a crucial business inquiry, unanswered calls cost you revenue and credibility. VioReceptionist is your AI-powered receptionist that is always available to seamlessly handle calls around the clock.
              </p>
            </div>

            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-[#112d56] mb-8">The Problem:</h3>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              
              {/* Problem 1 */}
              <Card className="border-0 shadow-lg bg-red-50 border-l-4 border-red-500">
                <CardHeader className="text-center">
                  <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
                  <CardTitle className="text-xl font-bold text-gray-800">80% of callers won't leave a voicemail</CardTitle>
                  <CardDescription className="text-gray-600">
                    —they'll call your competitor instead.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Problem 2 */}
              <Card className="border-0 shadow-lg bg-orange-50 border-l-4 border-orange-500">
                <CardHeader className="text-center">
                  <Clock className="h-12 w-12 text-orange-600 mx-auto mb-4" />
                  <CardTitle className="text-xl font-bold text-gray-800">Small business owners lose up to 8 hours a week</CardTitle>
                  <CardDescription className="text-gray-600">
                    managing calls and appointments.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Problem 3 */}
              <Card className="border-0 shadow-lg bg-yellow-50 border-l-4 border-yellow-500">
                <CardHeader className="text-center">
                  <PhoneCall className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
                  <CardTitle className="text-xl font-bold text-gray-800">Missed calls, slow responses, and inconsistent service</CardTitle>
                  <CardDescription className="text-gray-600">
                    damage your reputation and cost you sales.
                  </CardDescription>
                </CardHeader>
              </Card>

            </div>

            {/* The Solution */}
            <div className="text-center mb-16">
              <h3 className="text-3xl font-bold text-[#112d56] mb-8">The Solution: VioReceptionist</h3>
              
              <div className="grid md:grid-cols-3 gap-8 mb-8">
                <div className="flex items-center justify-center space-x-3">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                  <span className="text-lg font-semibold text-gray-800">Never miss a call again.</span>
                </div>
                <div className="flex items-center justify-center space-x-3">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                  <span className="text-lg font-semibold text-gray-800">Boost customer satisfaction with instant responses.</span>
                </div>
                <div className="flex items-center justify-center space-x-3">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                  <span className="text-lg font-semibold text-gray-800">Free up your time to focus on growing your business.</span>
                </div>
              </div>

              <div className="bg-gradient-to-r from-[#112d56] to-[#1a3d6b] text-white p-8 rounded-xl">
                <p className="text-xl leading-relaxed">
                  Imagine having a dedicated professional voice receptionist who never lets potential customers slip away — how much could your business grow when every call is answered promptly and professionally?
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {benefits.map((benefit, index) => (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 text-center bg-gradient-to-br from-white to-gray-50">
                  <CardHeader className="pb-4">
                    <CheckCircle className="h-8 w-8 text-[#ff7a1f] mx-auto mb-3" />
                    <CardTitle className="text-lg font-bold text-[#112d56]">{benefit}</CardTitle>
                  </CardHeader>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Button 
                size="lg" 
                className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-8 py-6 text-xl rounded-xl"
                onClick={handleDemo}
              >
                <Calendar className="mr-3 h-6 w-6" />
                SCHEDULE A DEMO
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Who Is <span className="text-[#ff7a1f]">VioReceptionist</span> For?
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto mb-8">
                You're juggling calls, bookings, and customers — but there's only so much you can do.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto mb-8">
                Missed calls mean lost business. Constant interruptions drain your focus. And hiring extra staff? Costly and complex.
              </p>
              <p className="text-lg text-gray-700 font-semibold max-w-4xl mx-auto">
                VioReceptionist is for business owners who want to protect their time, serve more customers, and stay in control — without the chaos.
              </p>
            </div>

            <h3 className="text-2xl font-bold text-[#112d56] mb-8 text-center">Perfect for:</h3>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {targetAudience.map((audience, index) => {
                const IconComponent = audience.icon;
                return (
                  <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardHeader>
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-[#ff7a1f]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                          <IconComponent className="h-6 w-6 text-[#ff7a1f]" />
                        </div>
                        <div>
                          <CardTitle className="text-xl font-bold text-[#112d56] mb-2">📞 {audience.title}</CardTitle>
                          <CardDescription className="text-gray-600 leading-relaxed">
                            {audience.description}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>

            <div className="text-center bg-[#112d56] text-white p-8 rounded-xl">
              <p className="text-xl leading-relaxed mb-4">
                You don't need to do it all.
              </p>
              <p className="text-lg">
                Let the parrot protect your time, grow your business, and give every caller a five-star experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Polly Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Meet Polly, your friendly and professional receptionist and customer service agent.
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
                Polly excels in making your customers and clients feel valued and heard. Polly is a perfect fit for resolving issues with empathy and providing reassurance in a stressful situation.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h3 className="text-2xl font-bold text-[#112d56] mb-6">Polly's character and persona traits:</h3>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Target className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-800">Purpose:</span>
                      <span className="text-gray-600 ml-2">Customer service and support</span>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Volume2 className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-800">Tone:</span>
                      <span className="text-gray-600 ml-2">Friendly and approachable, calm and reassuring</span>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Users className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-800">Audience:</span>
                      <span className="text-gray-600 ml-2">Customers and clients seeking support or information</span>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Clock className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-800">Pace:</span>
                      <span className="text-gray-600 ml-2">Moderate, ensuring clarity and comfort</span>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Settings className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-800">Complexity Handling:</span>
                      <span className="text-gray-600 ml-2">Balances simplicity with thoroughness based on user needs</span>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <Star className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-800">Traits:</span>
                      <span className="text-gray-600 ml-2">Empathetic, patient, and caring</span>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <MessageCircle className="h-5 w-5 text-[#ff7a1f] mt-1 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-gray-800">Keywords/Phrases:</span>
                      <span className="text-gray-600 ml-2">"I'm here to help.", "Let's resolve this together.", "Thank you for bringing this to our attention."</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
                  {/* Video Player Header */}
                  <div className="bg-gradient-to-r from-[#112d56] to-[#1a3d6b] px-6 py-4 flex items-center space-x-3">
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    </div>
                    <div className="text-white/70 text-sm font-medium">Meet Polly</div>
                    <div className="flex items-center space-x-2">
                      <Play className="h-4 w-4 text-white/70" />
                    </div>
                  </div>
                  
                  {/* Polly Video - Using correct video ID */}
                  <div className="bg-black relative">
                    <div className="relative" style={{ paddingBottom: '56.25%' }}>
                      <iframe 
                        src="https://api.vadoo.tv/iframe_test?id=7LISH0p6j0p7wHujo2GikNQz2XB8TnXm" 
                        scrolling="no" 
                        style={{ 
                          border: 0, 
                          top: 0, 
                          left: 0, 
                          width: '100%', 
                          height: '100%', 
                          position: 'absolute',
                          overflow: 'hidden',
                          borderRadius: '5px'
                        }} 
                        allowFullScreen="1" 
                        allow="autoplay"
                        title="Meet Polly Video"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Audio Demos Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Listen To Polly in Action: Smart, Seamless, and Professional Call Handling!
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto mb-8">
                Listen to how VioReceptionist enhances customer interactions for a business called "SuccessEdge Business Coaching" through three real-world call scenarios.
              </p>
              
              <div className="text-left max-w-4xl mx-auto space-y-4 mb-8">
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">First,</span> Polly greets a caller and seamlessly transfers them to a human agent, ensuring a smooth and professional handover.
                </p>
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Next,</span> she guides a caller through the appointment booking process, making scheduling effortless.
                </p>
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">Finally,</span> Polly demonstrates how VioReceptionist serves as a business knowledge hub, providing accurate and in-depth answers to customer inquiries.
                </p>
              </div>
              
              <p className="text-xl font-semibold text-[#ff7a1f]">
                Listen now to hear Polly in action!
              </p>
            </div>

            <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8">
              {audioDemos.map((demo, index) => (
                <Card key={demo.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardHeader>
                    <CardTitle className="text-xl font-bold text-[#112d56] mb-4">{demo.title}</CardTitle>
                    <CardDescription className="text-gray-600 leading-relaxed mb-6">
                      {demo.description}
                    </CardDescription>
                    
                    <div className="space-y-4">
                      <audio 
                        controls 
                        className="w-full"
                        preload="metadata"
                      >
                        <source src={demo.audioUrl} type="audio/mpeg" />
                        Your browser does not support the audio element.
                      </audio>
                      
                      <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                        <Volume2 className="h-4 w-4" />
                        <span>High Quality Audio</span>
                        <span>•</span>
                        <span>Polly Voice Demo</span>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Brochure Download Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Download the <span className="text-[#ff7a1f]">VioReceptionist</span> Brochure
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
                This brochure contains an overview of our VioReceptionist voice automation systems and a detailed breakdown of features across our three service tiers levels:
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              
              {/* Basic Tier */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-[#112d56] mb-4">Basic</CardTitle>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    AI call answering, professional greetings, voicemail transcription, and seamless call routing.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Plus Tier */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-[#112d56] mb-4">Plus</CardTitle>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    Smart appointment scheduling, automated follow-ups, and advanced call handling for a smoother customer experience.
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Pro Tier */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-[#112d56] mb-4">Pro</CardTitle>
                  <CardDescription className="text-gray-600 leading-relaxed">
                    AI-powered call intelligence, CRM & API integrations, real-time analytics, multi-language support, and even payment processing.
                  </CardDescription>
                </CardHeader>
              </Card>

            </div>

            <div className="text-center">
              <div className="max-w-md mx-auto mb-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_dfe52526-e7e5-4eb7-a28d-2b3a074d8379/artifacts/metv2tuj_brouchure.png" 
                  alt="VioReceptionist Brochure Cover"
                  className="w-full rounded-lg shadow-lg"
                />
              </div>
              <Button 
                size="lg" 
                className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-8 py-6 text-xl rounded-xl"
              >
                <Download className="mr-3 h-6 w-6" />
                Download Brochure
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Data-Driven Success Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                🚀 Data-Driven Success: How Smart Parrot Concierge Delivers Results
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto">
                Voice AI-powered voice automation is revolutionising the way businesses handle customer interactions. With advancements in voice AI-driven call management, companies across various industries are experiencing increased efficiency, improved customer satisfaction, and significant cost savings. The following statistics highlight the tangible impact of voice AI receptionists demonstrating why businesses are rapidly adopting this technology to stay competitive.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              
              {/* Efficiency & Cost Savings */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-[#112d56] mb-4 flex items-center">
                    📈 Increased Efficiency & Cost Savings
                  </CardTitle>
                  <CardContent className="p-0">
                    <ul className="space-y-3 text-gray-700">
                      <li>• 40% reduction in missed calls, ensuring more customer inquiries convert into sales.</li>
                      <li>• Up to 60% cost savings compared to hiring a full-time receptionist.</li>
                      <li>• 24/7 availability – never miss a call, even outside business hours.</li>
                    </ul>
                  </CardContent>
                </CardHeader>
              </Card>

              {/* Customer Experience */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-[#112d56] mb-4 flex items-center">
                    📊 Improved Customer Experience
                  </CardTitle>
                  <CardContent className="p-0">
                    <ul className="space-y-3 text-gray-700">
                      <li>• 90% faster response times than traditional phone support.</li>
                      <li>• 3X higher customer satisfaction with instant answers and seamless scheduling.</li>
                      <li>• 80% of customers prefer businesses that offer quick, AI-assisted service.</li>
                    </ul>
                  </CardContent>
                </CardHeader>
              </Card>

              {/* Revenue & Productivity */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-[#112d56] mb-4 flex items-center">
                    🚀 Boosted Revenue & Productivity
                  </CardTitle>
                  <CardContent className="p-0">
                    <ul className="space-y-3 text-gray-700">
                      <li>• 50% increase in booked appointments through automated scheduling.</li>
                      <li>• 25% more upsell opportunities by ensuring every inquiry is handled professionally.</li>
                      <li>• Zero wait times – customers get immediate responses without being placed on hold.</li>
                    </ul>
                  </CardContent>
                </CardHeader>
              </Card>

            </div>

            <div className="text-center mt-12">
              <p className="text-xl font-semibold text-[#112d56]">
                With VioReceptionist, your business stays ahead—more calls answered, more customers served, and more revenue generated.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Say Goodbye Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
              Say Goodbye to Inefficiencies — Embrace Voice AI-Driven Operations
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto mb-8">
              It's time to leave the frustrations of the "old way" behind. With Smart Parrot Voice Systems, you'll experience a seamless, AI-driven approach that transforms your business operations.
            </p>
            
            <h3 className="text-2xl font-bold text-[#ff7a1f] mb-6">The Future is Here</h3>
            <p className="text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto">
              Transform your business with AI-powered efficiency that works around the clock, ensuring no opportunity is ever missed.
            </p>
          </div>
        </div>
      </section>

      {/* Demo CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
              Book a Demo and See VioReceptionist and Voice Automation Systems In Action.
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed max-w-4xl mx-auto mb-8">
              Stop letting missed calls hurt your business. Book a free demo today and experience how VioReceptionist can help you:
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-800">Convert more leads</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-800">Improve customer service</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-800">Free up your team's time</p>
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-gray-800">Save money on staffing</p>
              </div>
            </div>

            <Button 
              size="lg" 
              className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-8 py-6 text-xl rounded-xl"
              onClick={handleDemo}
            >
              <Calendar className="mr-3 h-6 w-6" />
              SCHEDULE A DEMO
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Your Questions Answered
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                You can browse the frequently asked questions or let Polly read them aloud by playing the audio file.
              </p>
            </div>

            <div className="space-y-12">
              {faqCategories.map((category, categoryIndex) => (
                <div key={categoryIndex}>
                  <h3 className="text-2xl font-bold text-[#112d56] mb-8">{category.category}</h3>
                  
                  <div className="space-y-4">
                    {category.questions.map((faq, faqIndex) => {
                      const uniqueIndex = `${categoryIndex}-${faqIndex}`;
                      const isExpanded = expandedFAQ === uniqueIndex;
                      
                      return (
                        <Card key={uniqueIndex} className="border-0 shadow-md hover:shadow-lg transition-all duration-300">
                          <CardHeader 
                            className="cursor-pointer"
                            onClick={() => toggleFAQ(uniqueIndex)}
                          >
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-lg font-semibold text-[#112d56] flex items-center">
                                <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                                {faq.q}
                              </CardTitle>
                              {isExpanded ? (
                                <Minus className="h-5 w-5 text-gray-500" />
                              ) : (
                                <Plus className="h-5 w-5 text-gray-500" />
                              )}
                            </div>
                            
                            {isExpanded && (
                              <CardDescription className="text-gray-600 leading-relaxed mt-4 ml-8">
                                {faq.a}
                              </CardDescription>
                            )}
                          </CardHeader>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-16">
              <p className="text-lg text-gray-600 mb-8">
                If your questions aren't covered here, we'd be happy to answer them all during your VioReceptionist demo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#112d56] text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center space-x-2 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_vioq-content-update/artifacts/w40y3694_Smart%20AI%20Logo%20white.png"
                  alt="Smart AI Solutions"
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-blue-200 leading-relaxed mb-8 max-w-xs">
                Driving efficiency and customer excellence with VioSuite — AI-powered voice & knowledge solutions for modern business.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Product</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/features" className="hover:text-[#ff7a1f] transition-colors text-lg">Features</a></li>
                <li><a href="/use-cases" className="hover:text-[#ff7a1f] transition-colors text-lg">Use Cases</a></li>
                <li><a href="/products/vioreceptionist" className="hover:text-[#ff7a1f] transition-colors text-lg">VioReceptionist</a></li>
                <li><a href="/products/viocoach" className="hover:text-[#ff7a1f] transition-colors text-lg">VioCoach</a></li>
                <li><a href="/products/vioconcierge" className="hover:text-[#ff7a1f] transition-colors text-lg">VioConcierge</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Company</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/about" className="hover:text-[#ff7a1f] transition-colors text-lg">About</a></li>
                <li><a href="/contact" className="hover:text-[#ff7a1f] transition-colors text-lg">Contact</a></li>
                <li><a href="https://cal.com/smartaisolutions/vioreceptionist" target="_blank" rel="noopener noreferrer" className="hover:text-[#ff7a1f] transition-colors text-lg">Book Demo</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Legal</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-[#ff7a1f] transition-colors text-lg">Terms of Service</a></li>
                <li><a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          
          <Separator className="bg-blue-800/50 my-8" />
          
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-blue-200 text-lg">
              © 2025 VioQbase. All rights reserved.
            </p>
            <div className="flex space-x-8 text-blue-200 mt-4 md:mt-0">
              <a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a>
              <a href="/terms" className="hover:text-[#ff7a1f] transition-colors text-lg">Terms of Service</a>
              <a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ConciergePage;